import { SvgIcon } from "@/components/icon"
import { whenPageIdle } from "@/helpers/whenPageIdle"
import { Tooltip } from "antd"
import classNames from "classnames"
import { FC, useEffect, useRef, useState } from "react"
import "./Hero3D.less"
import type { HeroViewer } from "./createHeroViewer"
import { getHero3DSkipReason } from "./hero3dSupport"

export type Hero3DProps = {
  /** Static image rendered in the HTML; stays as the fallback whenever 3D can't or shouldn't run. */
  fallbackSrc: string
  fallbackAlt: string
  /** Accessible description of the visual, announced once whether the PNG or the 3D view is showing. */
  ariaLabel: string
  modelUrl: string
  className?: string
}

/** Give up (and keep the PNG) if the SDK + model haven't produced a first frame by then. */
const LOAD_TIMEOUT_MS = 10_000
/** Start fetching a bit before the hero scrolls into view. */
const PRELOAD_ROOT_MARGIN = "200px"
/** Share of the hero that must be on screen before the intro spin plays. */
const INTRO_VISIBLE_RATIO = 0.35
/**
 * The cross-fade from the image to the canvas (`@fade-duration` in Hero3D.less). The model holds still until it's done:
 * spinning while half-transparent over the differently posed image read as flashing.
 */
const FADE_IN_MS = 600

/** Page scroll (px) past which the model turns to its scrolled angle; back at or above it, it turns back. */
const SCROLL_TURN_THRESHOLD_PX = 40

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"
const MOBILE_QUERY = "(max-width: 767px), (pointer: coarse)"

const mediaQuery = (query: string) => (typeof window.matchMedia === "function" ? window.matchMedia(query) : null)

/**
 * Progressive-enhancement hero visual: renders the PNG first (SEO, LCP, no-JS), then — on capable devices, once the
 * hero is near the viewport — lazy-loads the WebGI/threepipe viewer and cross-fades to it after its first frame.
 */
const Hero3D: FC<Hero3DProps> = ({ fallbackSrc, fallbackAlt, ariaLabel, modelUrl, className }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [booting, setBooting] = useState(false)
  const [ready, setReady] = useState(false)

  // Decide whether to enhance at all, wait until the hero is close to the viewport, then until the page is idle.
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || typeof IntersectionObserver === "undefined") return
    if (getHero3DSkipReason() !== null) return

    let cancelIdleWait: (() => void) | undefined
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect()
          cancelIdleWait = whenPageIdle(() => setBooting(true))
        }
      },
      { rootMargin: PRELOAD_ROOT_MARGIN }
    )
    observer.observe(wrapper)
    return () => {
      observer.disconnect()
      cancelIdleWait?.()
    }
  }, [])

  // Boot the viewer once the canvas is mounted; any failure (import, load, timeout, context loss) drops back to the PNG.
  useEffect(() => {
    const canvas = canvasRef.current
    const wrapper = wrapperRef.current
    if (!booting || !canvas || !wrapper) return

    let cancelled = false
    let viewer: HeroViewer | null = null
    let isInView = true
    const cleanups: (() => void)[] = []

    const fallBack = () => {
      if (cancelled) return
      cancelled = true
      setReady(false)
      setBooting(false) // unmounts the canvas; this effect's cleanup disposes the viewer
    }

    // The timeout only runs while the tab is visible: browsers don't render frames in background tabs, so a page
    // opened in one would otherwise always time out before its first frame.
    let timeLeft = LOAD_TIMEOUT_MS
    let timerStartedAt = 0
    let timer: number | undefined
    const stopTimer = () => {
      if (timer === undefined) return
      window.clearTimeout(timer)
      timer = undefined
      timeLeft -= performance.now() - timerStartedAt
    }
    const syncTimer = () => {
      if (document.visibilityState !== "visible") return stopTimer()
      if (timer !== undefined) return
      timerStartedAt = performance.now()
      timer = window.setTimeout(() => {
        console.warn(`[Hero3D] no first frame after ${LOAD_TIMEOUT_MS}ms, keeping the static image`)
        fallBack()
      }, timeLeft)
    }
    const clearLoadTimeout = () => {
      stopTimer()
      document.removeEventListener("visibilitychange", syncTimer)
    }
    document.addEventListener("visibilitychange", syncTimer)
    syncTimer()

    const reducedMotion = mediaQuery(REDUCED_MOTION_QUERY)

    import("./createHeroViewer")
      .then(({ createHeroViewer }) =>
        createHeroViewer({
          canvas,
          modelUrl,
          isMobile: Boolean(mediaQuery(MOBILE_QUERY)?.matches),
          animate: !reducedMotion?.matches,
          onContextLost: fallBack,
        })
      )
      .then(created => {
        if (cancelled) {
          created.dispose()
          return
        }
        clearLoadTimeout()
        viewer = created

        let isMostlyVisible = false
        let fadedIn = false
        const syncActive = () => {
          const tabVisible = document.visibilityState === "visible"
          viewer?.setActive(isInView && tabVisible)
          // The intro spin plays once, the first time a good part of the hero is actually on screen.
          if (fadedIn && isMostlyVisible && tabVisible) viewer?.playIntro()
        }
        const fadeTimer = window.setTimeout(() => {
          fadedIn = true
          syncActive()
        }, FADE_IN_MS)
        cleanups.push(() => window.clearTimeout(fadeTimer))

        const visibilityObserver = new IntersectionObserver(
          entries => {
            const entry = entries[entries.length - 1]
            isInView = entry.isIntersecting
            isMostlyVisible = entry.isIntersecting && (entry.intersectionRatio ?? 1) >= INTRO_VISIBLE_RATIO
            syncActive()
          },
          { threshold: [0, INTRO_VISIBLE_RATIO] }
        )
        visibilityObserver.observe(wrapper)
        cleanups.push(() => visibilityObserver.disconnect())

        document.addEventListener("visibilitychange", syncActive)
        cleanups.push(() => document.removeEventListener("visibilitychange", syncActive))

        // Scroll turn: the model turns once the page leaves the top, and back when it returns. The viewer ignores
        // repeated values, so a scroll event costs one comparison until the threshold is actually crossed.
        const syncScrolled = () => viewer?.setScrolled(window.scrollY > SCROLL_TURN_THRESHOLD_PX)
        syncScrolled()
        window.addEventListener("scroll", syncScrolled, { passive: true })
        cleanups.push(() => window.removeEventListener("scroll", syncScrolled))

        if (reducedMotion) {
          const onMotionChange = (event: MediaQueryListEvent) => viewer?.setAnimate(!event.matches)
          reducedMotion.addEventListener("change", onMotionChange)
          cleanups.push(() => reducedMotion.removeEventListener("change", onMotionChange))
        }

        setReady(true)
      })
      .catch(error => {
        console.warn("[Hero3D] falling back to the static image:", error)
        fallBack()
      })

    return () => {
      cancelled = true
      clearLoadTimeout()
      cleanups.forEach(cleanup => cleanup())
      viewer?.dispose()
      viewer = null
    }
  }, [booting, modelUrl])

  return (
    <div
      ref={wrapperRef}
      className={classNames("hero-3d", { "hero-3d--ready": ready }, className)}
      role={"img"}
      aria-label={ariaLabel}
    >
      {/* The image always stays in the flow: it defines the box size, so swapping to 3D never shifts layout. */}
      <img className={"hero-3d__fallback"} src={fallbackSrc} alt={fallbackAlt} />
      {booting && <canvas ref={canvasRef} className={"hero-3d__canvas"} aria-hidden={"true"} />}
      {/* Interaction hint — only once the model is live; the static image can't be rotated. */}
      {ready && (
        <Tooltip title={"Drag the image to rotate it"}>
          <span className={"hero-3d__rotate-hint"}>
            <SvgIcon type={"rotate-orbit"} color={"white"} />
          </span>
        </Tooltip>
      )}
    </div>
  )
}

export default Hero3D
